import { defineStore } from 'pinia';
import { strategyApi } from '@/services/strategyService';

export const useInflowOutflowStore = defineStore('inflowOutflow', {
  state: () => ({
    overviewData: null,
    overviewLoading: false,

    activityData: {
      increasing: [],
      decreasing: [],
    },
    activityLoading: false,

    detailsData: [],
    detailsLoading: false,

    worklists: [],
    worklistsLoading: false,

    selectedWorklists: [],
    showBalance: false,
    selectedStrategyId: 1,

    filters: {
      active: true,
      worklistTypes: [],
      startDate: null,
      endDate: null,
    },

    detailPagination: {
      page: 1,
      pageSize: 13,
      sortKey: null,
      sortOrder: null,
      rowCount: 0,
    },
  }),

  actions: {
    async fetchOverview(worklistIds = null) {
      this.overviewLoading = true;
      try {
        const params = new URLSearchParams();
        if (worklistIds?.length) {
          params.append('work_lists', worklistIds.join(','));
        }
        const data = await strategyApi.getInflowOutflowOverview(params);
        this.overviewData = data;
      } catch (error) {
        console.error('Error fetching overview:', error);
      } finally {
        this.overviewLoading = false;
      }
    },

    async fetchActivity() {
      this.activityLoading = true;
      try {
        const data = await strategyApi.getInflowOutflowActivity();
        // Normalize API shape { top_increase: [], top_decrease: [] } -> { increasing: [], decreasing: [] }
        if (data && (data.top_increase || data.top_decrease)) {
          this.activityData = {
            increasing: data.top_increase || [],
            decreasing: data.top_decrease || [],
          };
        } else {
          this.activityData = data || { increasing: [], decreasing: [] };
        }
      } catch (error) {
        console.error('Error fetching activity:', error);
      } finally {
        this.activityLoading = false;
      }
    },

    async fetchDetails(page = 1, sortKey = null, sortOrder = null) {
      this.detailsLoading = true;
      try {
        const params = new URLSearchParams();
        if (this.selectedWorklists?.length) {
          params.append('work_lists', this.selectedWorklists.join(','));
        }
        if (this.filters.worklistTypes?.length) {
          params.append('work_list_type', this.filters.worklistTypes.join(','));
        }
        if (this.filters.startDate) {
          params.append('start_date', this.filters.startDate);
        }
        if (this.filters.endDate) {
          params.append('end_date', this.filters.endDate);
        }
        if (sortKey) {
          params.append('sort_key', sortKey);
          this.detailPagination.sortKey = sortKey;
        } else if (this.detailPagination.sortKey) {
          params.append('sort_key', this.detailPagination.sortKey);
        }
        if (sortOrder) {
          params.append('sort_order', sortOrder);
          this.detailPagination.sortOrder = sortOrder;
        } else if (this.detailPagination.sortOrder) {
          params.append('sort_order', this.detailPagination.sortOrder);
        }
        params.append('page', page);
        params.append('active', this.filters.active);
        const data = await strategyApi.getInflowOutflowDetails(params);
        this.detailsData = data?.details || [];
        this.detailPagination.rowCount = data.total_count || 0;
        this.detailPagination.page = page;
      } catch (error) {
        console.error('Error fetching details:', error);
      } finally {
        this.detailsLoading = false;
      }
    },

    async fetchWorklists(src) {
      this.worklistsLoading = true;
      try {
        const params = new URLSearchParams();
        if (src === 'overview') {
          params.append('active', 'true');
        }

        // Include unassigned worklists
        params.append('include_unassigned', 'true');
        const data = await strategyApi.getInflowOutflowWorklists(params);
        // Ensure array shape for consumers using .map/.reduce
        let worklists = Array.isArray(data) ? data : [];

        // Filter out future-dated and expired worklists
        const now = new Date();
        worklists = worklists.filter(worklist => {
          // Include if no start/end dates (unassigned or always active)
          if (!worklist.start_timest && !worklist.end_timest) {
            return true;
          }

          // Check if worklist is currently active (not future, not expired)
          const startDate = worklist.start_timest
            ? new Date(worklist.start_timest)
            : null;
          const endDate = worklist.end_timest
            ? new Date(worklist.end_timest)
            : null;

          // If it has a start date and it's in the future, exclude it
          if (startDate && startDate > now) {
            return false;
          }

          // If it has an end date and it's expired, exclude it
          if (endDate && endDate < now) {
            return false;
          }

          return true;
        });

        this.worklists = worklists;
      } catch (error) {
        console.error('Error fetching worklists:', error);
      } finally {
        this.worklistsLoading = false;
      }
    },
  },

  getters: {
    isCurrency: state => state.showBalance,
    chartData: state => {
      // Support array shape returned by service
      // [{ total_inflow_count, total_outflow_count, week_start, ...}, ...]
      if (Array.isArray(state.overviewData)) {
        // Sort newest->oldest or oldest->newest? We need ASC (earliest first)
        const sorted = [...state.overviewData].sort(
          (a, b) => new Date(a.week_start) - new Date(b.week_start)
        );

        let categories = sorted.map(item =>
          fmt(item.week_start + ' 12:00:00', item.week_end + ' 12:00:00')
        );

        const inflow = state.showBalance
          ? sorted.map(i => Number(i.total_inflow_balance || 0))
          : sorted.map(i => Number(i.total_inflow_count || 0));

        const outflow = state.showBalance
          ? sorted.map(i => Number(i.total_outflow_balance || 0))
          : sorted.map(i => Number(i.total_outflow_count || 0));

        // Total is the weekly sum of current totals across selected worklists
        const total = state.showBalance
          ? sorted.map(i => Number(i.total_balance || 0))
          : sorted.map(i => Number(i.total_count || 0));

        // Only show weeks with actual data - no padding
        return {
          series: [
            {
              name: state.showBalance ? 'Balance Added' : 'Items Added',
              type: 'column',
              data: inflow,
            },
            {
              name: state.showBalance ? 'Balance Removed' : 'Items Removed',
              type: 'column',
              data: outflow,
            },
            {
              name: state.showBalance ? 'Total Balance' : 'Total Count',
              type: 'area',
              data: total,
            },
          ],
          categories: categories,
        };
      }

      // Legacy shape fallback
      if (!state.overviewData) return { series: [], categories: [] };
      return {
        series: [
          {
            name: 'Items Added',
            type: 'column',
            data: state.overviewData.added_items,
          },
          {
            name: 'Items Removed',
            type: 'column',
            data: state.overviewData.removed_items,
          },
          {
            name: 'Total Count',
            type: 'area',
            data: state.overviewData.total_items,
          },
        ],
        categories: state.overviewData.date_ranges,
      };
    },

    // Filtered down worklists for detail dropdown
    detailWorklistsOptions: state => {
      let active = state.filters.active;
      let type = state.filters.worklistTypes;

      return state.worklists
        .filter(wl => wl.active === active)
        .filter(wl => (type?.length ? type.includes(wl.type) : true))
        .map(wl => ({
          id: wl.work_list_id || wl.id,
          label: wl.work_list_name,
        }));
    },
    getExportFilters: state => {
      return {
        work_list_ids: state.selectedWorklists,
        work_list_types: state.filters.worklistTypes,
        start_date: state.filters.startDate,
        end_date: state.filters.endDate,
        active: state.filters.active,
      };
    },
  },
});

function fmt(start, end) {
  start = new Date(start);
  end = new Date(end);
  const startMM = `${start.getMonth() + 1}`.padStart(2, '0');
  const startDD = `${start.getDate()}`.padStart(2, '0');
  const endMM = `${end.getMonth() + 1}`.padStart(2, '0');
  const endDD = `${end.getDate()}`.padStart(2, '0');
  return `${startMM}/${startDD} - ${endMM}/${endDD}`;
}
